/** Trail — プレイヤーが自陣外に出た時の軌跡を管理。
 *  Reference: docs/STAGE_DESIGN.md §1.1 trail
 *  - 自陣に再接続した瞬間、囲ったエリアを領地化(処理は GameLoop 側)
 *  - 切断条件: 敵接触 / 自分のトレイル横切り(初期は無効化)
 */

import { Container, Graphics } from 'pixi.js';
import type { GridConfig, GridPos } from './types';
import { gridEquals, gridIndex } from './types';

export class Trail {
  public readonly view: Container;

  private readonly grid: GridConfig;
  private readonly color: number;
  private readonly graphics: Graphics;
  private cells: GridPos[] = [];
  private cellSet: Uint8Array;
  private visualEndpoint: { x: number; y: number } | null = null;

  constructor(grid: GridConfig, color: number) {
    this.grid = grid;
    this.color = color;
    this.cellSet = new Uint8Array(grid.gridSize * grid.gridSize);

    this.view = new Container();
    this.graphics = new Graphics();
    this.view.addChild(this.graphics);
  }

  /** トレイルにマスを追加。直前と同一なら無視(同マス連続記録防止)。 */
  add(pos: GridPos): void {
    if (this.cells.length > 0 && gridEquals(this.cells[this.cells.length - 1]!, pos)) {
      return;
    }
    this.cells.push(pos);
    this.cellSet[gridIndex(pos, this.grid.gridSize)] = 1;
    this.redraw();
  }

  contains(pos: GridPos): boolean {
    return this.cellSet[gridIndex(pos, this.grid.gridSize)] === 1;
  }

  getCells(): readonly GridPos[] {
    return this.cells;
  }

  isEmpty(): boolean {
    return this.cells.length === 0;
  }

  clear(): void {
    this.cells = [];
    this.cellSet.fill(0);
    this.visualEndpoint = null;
    this.graphics.clear();
  }

  /** プレイヤーの現在の視覚位置(サブセル単位)を渡してトレイルを末端まで追従描画。
   *  毎フレーム呼ぶ想定。トレイルが空なら何もしない。
   */
  setVisualEndpoint(x: number, y: number): void {
    if (this.cells.length === 0) {
      if (this.visualEndpoint !== null) {
        this.visualEndpoint = null;
      }
      return;
    }
    this.visualEndpoint = { x, y };
    this.redraw();
  }

  /** 決定論的な擬似乱数(セル座標シード)。フレーム間で揺れない wobble を作る。 */
  private deterministicJitter(seed: number): number {
    let t = (seed * 1664525 + 1013904223) >>> 0;
    t ^= t >>> 16;
    t = Math.imul(t, 0x85ebca6b) >>> 0;
    t ^= t >>> 13;
    return ((t % 1000) / 500 - 1);
  }

  private redraw(): void {
    this.graphics.clear();
    if (this.cells.length === 0) return;

    const cp = this.grid.cellPx;

    // セル中心 + わずかな wobble(筆圧のブレ風)
    const points = this.cells.map((c, i) => {
      const jx = this.deterministicJitter((c.x * 73856093) ^ (c.y * 19349663) ^ i);
      const jy = this.deterministicJitter((c.x * 83492791) ^ (c.y * 60493) ^ (i + 17));
      return {
        x: (c.x + 0.5) * cp + jx * cp * 0.055,
        y: (c.y + 0.5) * cp + jy * cp * 0.055,
      };
    });

    // プレイヤー現在位置までトレイルを伸ばす(末端の隙間解消、ここは wobble なし)
    if (this.visualEndpoint) {
      points.push({ x: this.visualEndpoint.x, y: this.visualEndpoint.y });
    }

    if (points.length >= 2) {
      // 3 重塗り(ベース太線・中間色・ハイライト細線)で「絵筆で塗った」質感
      const passes = [
        { width: cp * 0.9, alpha: 0.2, offsetX: 0, offsetY: cp * 0.055 },
        { width: cp * 0.74, alpha: 0.76, offsetX: 0, offsetY: 0 },
        { width: cp * 0.24, alpha: 0.34, offsetX: -cp * 0.045, offsetY: -cp * 0.05 },
      ];

      for (const pass of passes) {
        this.graphics.moveTo(points[0]!.x + pass.offsetX, points[0]!.y + pass.offsetY);
        for (let i = 1; i < points.length; i++) {
          this.graphics.lineTo(points[i]!.x + pass.offsetX, points[i]!.y + pass.offsetY);
        }
        this.graphics.stroke({
          width: pass.width,
          color: this.color,
          alpha: pass.alpha,
          cap: 'round',
          join: 'round',
        });
      }

      // 軌跡先端のドリップ(塗料の垂れ)= プレイヤーの少し後方に小円
      const tip = points[points.length - 1]!;
      const prev = points[Math.max(0, points.length - 2)]!;
      const dx = tip.x - prev.x;
      const dy = tip.y - prev.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      const nx = -uy;
      const ny = ux;
      const backX = tip.x - ux * cp * 0.34;
      const backY = tip.y - uy * cp * 0.34;
      this.graphics.circle(backX, backY, cp * 0.2);
      this.graphics.fill({ color: this.color, alpha: 0.78 });
      this.graphics.circle(backX + nx * cp * 0.15, backY + ny * cp * 0.1, cp * 0.09);
      this.graphics.fill({ color: this.color, alpha: 0.44 });
      this.graphics.circle(backX - nx * cp * 0.11, backY - ny * cp * 0.16, cp * 0.055);
      this.graphics.fill({ color: this.color, alpha: 0.34 });
      this.graphics.circle(backX - cp * 0.045, backY - cp * 0.05, cp * 0.07);
      this.graphics.fill({ color: 0xffffff, alpha: 0.42 });
    } else {
      // 1 点だけの時は円で描画(出始めの「インク垂らし」風)
      const p = points[0]!;
      this.graphics.circle(p.x, p.y, cp * 0.42);
      this.graphics.fill({ color: this.color, alpha: 0.62 });
      this.graphics.circle(p.x + cp * 0.14, p.y + cp * 0.1, cp * 0.13);
      this.graphics.fill({ color: this.color, alpha: 0.32 });
      this.graphics.circle(p.x - cp * 0.1, p.y - cp * 0.1, cp * 0.12);
      this.graphics.fill({ color: 0xffffff, alpha: 0.4 });
    }
  }
}
